import { Margin, usePDF } from "react-to-pdf";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { axiosInstance } from "../../../../utils/networking";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

// Rotating palette for the assistance-category segments.
const CATEGORY_COLORS = [
  PRIMARY,
  "#3F51B5",
  "#00897b",
  "#fdb814",
  "#c2185b",
  "#5c6bc0",
  "#26a69a",
  "#ef6c00",
  "#7e57c2",
  "#43a047",
  "#8d6e63",
  "#9e9e9e",
];

// ---- Small presentational helpers -------------------------------------------

const SectionCard = ({ children, sx }: { children: any; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionTitle = ({
  children,
  subtitle,
}: {
  children: any;
  subtitle?: string;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#2c2c34",
        borderLeft: `4px solid ${PRIMARY}`,
        pl: 1.5,
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", pl: 1.5, mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const StatCard = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: any;
  accent: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 180px",
      p: 2.5,
      borderRadius: "16px",
      border: CARD_BORDER,
      borderTop: `3px solid ${accent}`,
      backgroundColor: "white",
      textAlign: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: "2rem",
        fontWeight: 800,
        color: "#2c2c34",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: "#8a8a93",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
  </Paper>
);

const ChartCard = ({
  title,
  subtitle,
  height,
  children,
}: {
  title: string;
  subtitle?: string;
  height: number | string;
  children: any;
}) => (
  <SectionCard sx={{ flex: "1 1 380px", minWidth: 0 }}>
    <SectionTitle subtitle={subtitle}>{title}</SectionTitle>
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </Box>
  </SectionCard>
);

const EngagementOperationsReport = () => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchReport = (start: Dayjs | null, end: Dayjs | null) => {
    setLoading(true);
    let url = `/reports/engagement_operations`;
    const params: string[] = [];
    if (start) params.push(`startDate=${start.format("YYYY-MM-DD")}`);
    if (end) params.push(`endDate=${end.format("YYYY-MM-DD")}`);
    if (params.length > 0) url = url + `?` + params.join("&");
    axiosInstance
      .get(url)
      .then((response: any) => {
        setReport(response.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReport(null, null);
  }, []);

  const { toPDF, targetRef } = usePDF({
    filename: "Engagement_Operations_Report.pdf",
    page: { margin: Margin.SMALL, format: "letter" },
    overrides: {
      canvas: {
        removeContainer: false,
      },
    },
  });

  const handleCsvDownload = () => {
    if (!report) return;
    const byAdvisor: any[] = report.byAdvisor || [];
    const csvContent: any[] = [];
    csvContent.push(["Advisor", "Open", "Completed", "Total"]);
    for (const row of byAdvisor) {
      csvContent.push([row.advisor_name, row.open, row.completed, row.total]);
    }

    let csvString = ``;
    for (const row of csvContent) {
      let rowString = `\n`;
      for (const item of row) {
        rowString = rowString + `"${item}",`;
      }
      csvString = csvString + rowString;
    }

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `engagement_operations_report.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    axiosInstance.post("/export_log", {
      export_type: "engagement_operations_report",
      record_count: byAdvisor.length,
      filters: {
        report: "EngagementOperations",
        startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
        endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
      },
    });
  };

  const rangeLabel =
    startDate || endDate
      ? `${startDate ? startDate.format("MMM D, YYYY") : "Beginning"} – ${
          endDate ? endDate.format("MMM D, YYYY") : "Today"
        }`
      : "All time";

  const toolbar = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: "16px",
        border: CARD_BORDER,
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            disableFuture={true}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            shouldDisableDate={(date: Dayjs) =>
              startDate !== null && date < startDate
            }
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          onClick={() => fetchReport(startDate, endDate)}
          sx={{
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: "#7A1B4D" },
          }}
        >
          Apply
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setStartDate(null);
            setEndDate(null);
            fetchReport(null, null);
          }}
        >
          Reset
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => {
            toPDF();
            axiosInstance.post("/export_log", {
              export_type: "report_pdf",
              record_count: report ? (report.byAdvisor || []).length : 0,
              filters: {
                report: "EngagementOperations",
                startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
                endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
              },
            });
          }}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleCsvDownload}
        >
          CSV
        </Button>
      </Box>
    </Paper>
  );

  const pageSx = {
    backgroundColor: "#f6f7f9",
    p: { xs: 2, md: 3 },
    minHeight: "100%",
  };

  if (loading && !report) {
    return (
      <Box
        sx={{
          ...pageSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  const summary = report?.summary || {};
  const casesByMonth: any[] = report?.casesByMonth || [];
  const byAdvisor: any[] = report?.byAdvisor || [];
  const activityMix: any[] = report?.activityMix || [];

  return (
    <Box sx={pageSx}>
      {toolbar}
      <Box ref={targetRef}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#2c2c34" }}>
            Engagement Operations Report
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
            {rangeLabel}
          </Typography>
        </Box>

        {!report || summary.total_cases === undefined ? (
          <SectionCard>
            <Typography sx={{ color: "#8a8a93" }}>
              No data available for the selected range.
            </Typography>
          </SectionCard>
        ) : (
          <>
            {/* Headline metrics */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mb: 3,
              }}
            >
              <StatCard
                label="Total Cases"
                value={summary.total_cases}
                accent={PRIMARY}
              />
              <StatCard
                label="Open"
                value={summary.open_cases}
                accent="#fdb814"
              />
              <StatCard
                label="Completed"
                value={summary.completed_cases}
                accent="#00897b"
              />
              <StatCard
                label="Active Advisors"
                value={summary.active_advisors}
                accent="#3F51B5"
              />
              <StatCard
                label="Avg Case Cycle"
                value={
                  summary.avg_case_duration_days == null
                    ? "—"
                    : Math.round(summary.avg_case_duration_days) + "d"
                }
                accent="#7e57c2"
              />
            </Box>

            {/* Case flow over time */}
            <Box sx={{ mb: 3 }}>
              <ChartCard
                title="Case Flow Over Time"
                subtitle="Cases created vs. closed by month"
                height={320}
              >
                <LineChart
                  data={casesByMonth}
                  margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="created"
                    name="Created"
                    stroke={PRIMARY}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="closed"
                    name="Closed"
                    stroke="#00897b"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ChartCard>
            </Box>

            {/* Advisor workload + activity mix */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                mb: 3,
              }}
            >
              <ChartCard
                title="Advisor Workload"
                subtitle="Open vs. completed cases per advisor (top 15)"
                height={Math.max(300, byAdvisor.length * 36)}
              >
                <BarChart
                  layout="vertical"
                  data={byAdvisor}
                  margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="advisor_name"
                    width={150}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="open"
                    name="Open"
                    stackId="a"
                    fill="#fdb814"
                  />
                  <Bar
                    dataKey="completed"
                    name="Completed"
                    stackId="a"
                    fill="#00897b"
                  />
                </BarChart>
              </ChartCard>

              <ChartCard
                title="Activity Mix"
                subtitle="Advisor–client engagement activity (excludes mass event invitations)"
                height={Math.max(300, activityMix.length * 34)}
              >
                <BarChart
                  layout="vertical"
                  data={activityMix}
                  margin={{ top: 8, right: 48, left: 16, bottom: 8 }}
                >
                  <XAxis type="number" allowDecimals={false} hide />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={180}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip formatter={(value: any) => [value, "Activities"]} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {activityMix.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                      />
                    ))}
                    <LabelList dataKey="count" position="right" />
                  </Bar>
                </BarChart>
              </ChartCard>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default EngagementOperationsReport;
